// Stripe service for handling payments and subscriptions
import stripePromise, { STRIPE_CONFIG } from '../config/stripe';

class StripeService {
  constructor() {
    this.stripe = null;
    this.initializeStripe();
  }

  async initializeStripe() {
    this.stripe = await stripePromise;
  }

  // Create a direct subscription (for payment modal)
  async createSubscription(userId, userEmail, userName, paymentMethodId) {
    try {
      const baseUrl = process.env.REACT_APP_WORKER_URL || 'https://google.shift.healthcare';
      const response = await fetch(`${baseUrl}/api/stripe/create-subscription`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userId,
          userEmail,
          userName,
          paymentMethodId,
          // Price ID will be determined by the worker
        }),
      });

      const data = await response.json();
      
      if (data.error) {
        throw new Error(data.error);
      }

      return data;
    } catch (error) {
      console.error('Error creating subscription:', error);
      throw error;
    }
  }

  // Create a checkout session for new subscriptions (legacy method)
  async createCheckoutSession(userId, userEmail, successUrl, cancelUrl) {
    try {
      const baseUrl = process.env.REACT_APP_WORKER_URL || 'https://google.shift.healthcare';
      const response = await fetch(`${baseUrl}/api/stripe/create-checkout-session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userId,
          userEmail,
          // Price ID will be determined by the worker
          successUrl,
          cancelUrl,
        }),
      });

      const session = await response.json();
      
      if (session.error) {
        throw new Error(session.error);
      }

      return session;
    } catch (error) {
      console.error('Error creating checkout session:', error);
      throw error;
    }
  }

  // Redirect to Stripe Checkout (legacy method)
  async redirectToCheckout(sessionId) {
    if (!this.stripe) {
      await this.initializeStripe();
    }

    const { error } = await this.stripe.redirectToCheckout({
      sessionId,
    });

    if (error) {
      throw new Error(error.message);
    }
  }

  // Create a billing portal session
  async createBillingPortalSession(customerId, returnUrl) {
    try {
      const baseUrl = process.env.REACT_APP_WORKER_URL || 'https://google.shift.healthcare';
      const response = await fetch(`${baseUrl}/api/stripe/create-portal-session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          customerId,
          returnUrl,
        }),
      });

      const session = await response.json();
      
      if (session.error) {
        throw new Error(session.error);
      }

      return session;
    } catch (error) {
      console.error('Error creating billing portal session:', error);
      throw error;
    }
  }

  // Redirect to billing portal
  async redirectToBillingPortal(sessionUrl) {
    window.location.href = sessionUrl;
  }

  // Get subscription status
  async getSubscriptionStatus(userId) {
    try {
      const baseUrl = process.env.REACT_APP_WORKER_URL || 'https://google.shift.healthcare';
      const response = await fetch(`${baseUrl}/api/stripe/subscription-status?userId=${userId}`);
      
      // Check if response is HTML (404 page) instead of JSON
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        // Stripe API not configured yet, return no subscription
        return {
          subscription: null,
          customer: null,
          status: 'no_customer'
        };
      }
      
      const data = await response.json();
      
      if (data.error) {
        throw new Error(data.error);
      }

      return data;
    } catch (error) {
      console.error('Error getting subscription status:', error);
      // Return no subscription if API is not available
      return {
        subscription: null,
        customer: null,
        status: 'no_customer'
      };
    }
  }

  // Get customer details
  async getCustomerDetails(customerId) {
    try {
      const response = await fetch(`/api/stripe/customer?customerId=${customerId}`);
      const data = await response.json();
      
      if (data.error) {
        throw new Error(data.error);
      }

      return data;
    } catch (error) {
      console.error('Error getting customer details:', error);
      throw error;
    }
  }

  // Get payment methods
  async getPaymentMethods(customerId) {
    try {
      const response = await fetch(`/api/stripe/payment-methods?customerId=${customerId}`);
      const data = await response.json();
      
      if (data.error) {
        throw new Error(data.error);
      }

      return data;
    } catch (error) {
      console.error('Error getting payment methods:', error);
      throw error;
    }
  }

  // Get invoices
  async getInvoices(customerId, limit = 10) {
    try {
      const response = await fetch(`/api/stripe/invoices?customerId=${customerId}&limit=${limit}`);
      const data = await response.json();
      
      if (data.error) {
        throw new Error(data.error);
      }

      return data;
    } catch (error) {
      console.error('Error getting invoices:', error);
      throw error;
    }
  }

  // Check if user has active subscription
  hasActiveSubscription(subscription) {
    if (!subscription) return false;
    
    const validStatuses = ['active', 'trialing'];
    return validStatuses.includes(subscription.status);
  }

  // Check if user is in grace period
  isInGracePeriod(subscription) {
    if (!subscription) return false;
    
    // Check if subscription is past_due and within grace period
    if (subscription.status === 'past_due') {
      const currentTime = new Date().getTime();
      const gracePeriodEnd = new Date(subscription.current_period_end * 1000).getTime() + (STRIPE_CONFIG.GRACE_PERIOD_DAYS * 24 * 60 * 60 * 1000);
      
      return currentTime < gracePeriodEnd;
    }
    
    return false;
  }

  // Check if user should be suspended
  shouldBeSuspended(subscription) {
    if (!subscription) return true;
    
    // Suspend if subscription is canceled, unpaid, or past grace period
    if (subscription.status === 'canceled' || subscription.status === 'unpaid') {
      return true;
    }
    
    if (subscription.status === 'past_due') {
      return !this.isInGracePeriod(subscription);
    }
    
    return false;
  }

  // Get subscription status for UI
  getSubscriptionStatusForUI(subscription) {
    if (!subscription) {
      return {
        status: 'inactive',
        message: 'No active subscription',
        canAccess: false,
        needsPayment: false,
        inGracePeriod: false
      };
    }

    const isActive = this.hasActiveSubscription(subscription);
    const inGracePeriod = this.isInGracePeriod(subscription);
    const shouldSuspend = this.shouldBeSuspended(subscription);

    if (isActive) {
      return {
        status: 'active',
        message: 'Subscription active',
        canAccess: true,
        needsPayment: false,
        inGracePeriod: false
      };
    }

    if (inGracePeriod) {
      return {
        status: 'past_due',
        message: 'Payment failed - please update your payment method',
        canAccess: true,
        needsPayment: true,
        inGracePeriod: true
      };
    }

    if (shouldSuspend) {
      return {
        status: 'suspended',
        message: 'Account suspended - please update payment method',
        canAccess: false,
        needsPayment: true,
        inGracePeriod: false
      };
    }

    return {
      status: 'unknown',
      message: 'Unknown subscription status',
      canAccess: false,
      needsPayment: false,
      inGracePeriod: false
    };
  }
}

// Export singleton instance
const stripeServiceInstance = new StripeService();
export default stripeServiceInstance;
